import { Loading } from "../../app/Status";
import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  DOC_CATEGORIES,
  DOC_CATEGORY_LABELS,
  DOC_STATUS_LABELS,
  DOC_VISIBILITY_LABELS,
  DRAWING_DISCIPLINES,
  type DocCategory,
  type DocStatus,
  type DocumentDto,
  type DocumentVersionDto,
  type DrawingDiscipline,
} from "@klantpunt/shared";
import { Tabs } from "../../app/Choice";
import { PageHeader } from "../../app/PageHeader";
import { buildingPath } from "../../app/navItems";
import {
  IconCamera,
  IconClose,
  IconDownload,
  IconHistory,
  IconMarkup,
  IconPencil,
  IconPin,
  IconSettings,
  IconShare,
  IconTrash,
  IconUpload,
} from "../../app/icons";
import { api } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useRealtime } from "../../lib/realtime";
import { useToast } from "../../lib/toast";
import { usePeople } from "../tasks/useTasks";
import { RequirementsView } from "./Requirements";
import { ScanDialog } from "./ScanDialog";
import { Logbook } from "../logbook/Logbook";
import { MarkupViewer } from "./MarkupViewer";
import { ShareDialog } from "./ShareDialog";
import { UsageDialog } from "./UsageFunctions";
import { useBuilding } from "../building/useBuilding";
import { EditDialog, errMsg, LinksDialog, TrashDialog, UploadDialog, VersionDialog } from "./DossierDialogs";
import { fileUrl, fmtDate, fmtSize, useDocTypes, useDocument, useDossier, useDossierRefresh } from "./useDossier";

type Tab = "documenten" | "tekeningen" | "verplicht" | "certificaten" | "plattegronden";
const TABS: Tab[] = ["documenten", "tekeningen", "verplicht", "certificaten", "plattegronden"];

type Dialog =
  | { type: "upload"; drawing?: boolean; typeKey?: string; files?: File[] }
  | { type: "scan"; typeKey?: string }
  | { type: "usage" }
  | { type: "version"; doc: DocumentDto }
  | { type: "edit"; doc: DocumentDto }
  | { type: "links"; doc: DocumentDto }
  | { type: "share"; doc: DocumentDto }
  | { type: "markup"; doc: DocumentDto; version: DocumentVersionDto; canEdit: boolean }
  | { type: "trash" };

const isPdfDoc = (v: DocumentVersionDto) => v.mime === "application/pdf";

/** Er zijn wijzigingen ingetekend op de huidige versie, maar nog geen nieuwe revisie. */
function MarkedChip() {
  return (
    <span className="chip doc-marked" title="Wijzigingen ingetekend; nog geen nieuwe revisie">
      Ingetekend
    </span>
  );
}

const STATUS_CLASS: Record<DocStatus, string> = { ok: "ok", expiring: "warn", expired: "expired" };

export function StatusChip({ doc }: { doc: Pick<DocumentDto, "status" | "current"> }) {
  if (!doc.current.validUntil) return <span className="muted">–</span>;
  return (
    <span className={`chip dir-cert dir-cert-${STATUS_CLASS[doc.status]}`} title={DOC_STATUS_LABELS[doc.status]}>
      {fmtDate(doc.current.validUntil)}
    </span>
  );
}

/**
 * Dossier van een gebouw (docs/DOSSIER.md): documenten in vaste rubrieken, tekeningen per discipline, en ter
 * inzage de certificaten uit keuringen en de plattegronden. Lijst links, paneel rechts (op de telefoon erover).
 */
export function DossierPage() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const tabRaw = params.get("tab") as Tab | null;
  const tab: Tab = tabRaw && TABS.includes(tabRaw) ? tabRaw : "documenten";
  const sel = params.get("document");
  const { data: b } = useBuilding(id);
  const dossier = useDossier(id, hasModule(me, "dossier"));
  const types = useDocTypes();
  useRealtime(id);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<DocCategory | "">("");
  const [disc, setDisc] = useState<DrawingDiscipline | "">("");
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const select = (next: { tab?: Tab; document?: string | null }) => {
    const p = new URLSearchParams(params);
    if (next.tab) {
      if (next.tab === "documenten") p.delete("tab");
      else p.set("tab", next.tab);
    }
    if (next.document !== undefined) {
      if (next.document) p.set("document", next.document);
      else p.delete("document");
    }
    setParams(p, { replace: true });
  };

  const all = useMemo(() => dossier.data?.documents ?? [], [dossier.data]);
  const docs = all.filter((d) => !d.type.drawing);
  const drawings = all.filter((d) => d.type.drawing);
  const needle = q.trim().toLowerCase();
  const match = (d: DocumentDto) =>
    !needle ||
    [
      d.title,
      d.type.name,
      d.reference,
      d.drawingNumber,
      d.issuer,
      d.current.fileName,
      ...d.links.map((l) => `${l.code ?? ""} ${l.name}`),
    ]
      .join(" ")
      .toLowerCase()
      .includes(needle);
  const attention = (d: DocumentDto) => d.status !== "ok";
  const docRows = docs.filter((d) => (!cat || d.type.category === cat) && (!onlyAttention || attention(d)) && match(d));
  const drawingRows = drawings
    .filter((d) => (!disc || d.discipline === disc) && match(d))
    .sort((a, c) => (a.drawingNumber ?? a.title).localeCompare(c.drawingNumber ?? c.title, "nl", { numeric: true }));
  const certs = (dossier.data?.certificates ?? []).filter(
    (c) =>
      !needle ||
      `${c.asset ? `${c.asset.code} ${c.asset.name}` : "Gebouw"} ${c.obligation} ${c.fileName}`
        .toLowerCase()
        .includes(needle),
  );
  const plans = dossier.data?.plans ?? [];

  if (!hasModule(me, "dossier"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Dossier.</p>
      </div>
    );
  if (!b || !dossier.data) {
    return <div className="center">{dossier.isError ? <p>{errMsg(dossier.error)}</p> : <Loading />}</div>;
  }
  const d = dossier.data;
  const canEdit = d.canEdit;
  const attentionCount = docs.filter(attention).length;
  const catCount = (c: DocCategory) => docs.filter((x) => x.type.category === c).length;
  const discCount = (x: DrawingDiscipline) => drawings.filter((y) => y.discipline === x).length;
  const hasSel = !!sel && (tab === "documenten" || tab === "tekeningen");

  return (
    <div className={`page dir-page doc-page ${hasSel ? "has-sel" : ""}`}>
      <PageHeader
        label={
          <>
            Dossier · <span className="code">{b.code}</span>
          </>
        }
        title={b.name}
        action={
          canEdit &&
          (tab === "documenten" || tab === "tekeningen") && (
            <button
              type="button"
              className="btn primary"
              onClick={() => setDialog({ type: "upload", drawing: tab === "tekeningen" })}
            >
              <IconUpload size={20} aria-hidden="true" /> Uploaden
            </button>
          )
        }
        menu={
          canEdit || d.canDelete || can(me, "document.types") ? (
            <>
              {canEdit && (
                <button type="button" className="menuitem" onClick={() => setDialog({ type: "scan" })}>
                  <IconCamera size={20} aria-hidden="true" />
                  Scannen met de camera
                </button>
              )}
              {d.canDelete && (
                <button type="button" className="menuitem" onClick={() => setDialog({ type: "trash" })}>
                  <IconTrash size={20} aria-hidden="true" />
                  Prullenbak{d.trashCount > 0 && ` (${d.trashCount})`}
                </button>
              )}
              {can(me, "document.types") && (
                <Link className="menuitem" to="/gebruikers?tab=documentsoorten">
                  <IconSettings size={20} aria-hidden="true" />
                  Documentsoorten
                </Link>
              )}
            </>
          ) : undefined
        }
        tabs={
          <Tabs
            label="Onderdeel"
            items={[
              { value: "documenten", label: "Documenten", count: docs.length },
              { value: "tekeningen", label: "Tekeningen", count: drawings.length },
              ...(canEdit || d.requirements.length
                ? [
                    {
                      value: "verplicht" as const,
                      label: "Verplicht",
                      count: d.requirements.filter((r) => r.state !== "ok" && r.state !== "not_applicable").length,
                    },
                  ]
                : []),
              ...(d.certificates.length
                ? [{ value: "certificaten" as const, label: "Certificaten", count: d.certificates.length }]
                : []),
              ...(plans.length
                ? [{ value: "plattegronden" as const, label: "Plattegronden", count: plans.length }]
                : []),
            ]}
            value={tab}
            onChange={(v) => select({ tab: v, document: null })}
          />
        }
      />

      {tab === "verplicht" ? (
        <RequirementsView
          dossier={d}
          buildingId={id}
          canEdit={canEdit}
          types={types.data ?? []}
          onUpload={(typeKey) => setDialog({ type: "upload", typeKey })}
          onUsage={() => setDialog({ type: "usage" })}
          onOpen={(docId) => select({ tab: "documenten", document: docId })}
        />
      ) : (
        <>
          <div className="dir-filters">
            <input
              type="search"
              className="dir-search"
              aria-label="Zoeken in het dossier"
              placeholder={tab === "tekeningen" ? "Zoeken op nummer, titel of asset" : "Zoek op titel of soort"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {tab === "documenten" && (
              <div className="dir-kinds" role="group" aria-label="Rubriek">
                <button
                  type="button"
                  className="kpi"
                  aria-pressed={!cat && !onlyAttention}
                  onClick={() => (setCat(""), setOnlyAttention(false))}
                >
                  Alles
                </button>
                {attentionCount > 0 && (
                  <button
                    type="button"
                    className="kpi doc-attn"
                    aria-pressed={onlyAttention}
                    onClick={() => setOnlyAttention((v) => !v)}
                  >
                    Verloopt of verlopen <span className="count num">{attentionCount}</span>
                  </button>
                )}
                {DOC_CATEGORIES.filter((c) => catCount(c) > 0).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="kpi"
                    aria-pressed={cat === c}
                    onClick={() => setCat(cat === c ? "" : c)}
                  >
                    {DOC_CATEGORY_LABELS[c]} <span className="count num">{catCount(c)}</span>
                  </button>
                ))}
              </div>
            )}
            {tab === "tekeningen" && (
              <div className="dir-kinds" role="group" aria-label="Discipline">
                <button type="button" className="kpi" aria-pressed={!disc} onClick={() => setDisc("")}>
                  Alles
                </button>
                {DRAWING_DISCIPLINES.filter((x) => discCount(x) > 0).map((x) => (
                  <button
                    key={x}
                    type="button"
                    className="kpi"
                    aria-pressed={disc === x}
                    onClick={() => setDisc(disc === x ? "" : x)}
                  >
                    {x} <span className="count num">{discCount(x)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="dir-layout">
            <div className="dir-list tscroll">
              {tab === "documenten" && (
                <>
                  <table className="it" aria-label="Documenten">
                    <thead>
                      <tr>
                        <th scope="col">Document</th>
                        <th scope="col" className="dir-hide-narrow">
                          Rubriek
                        </th>
                        <th scope="col" className="num dir-hide-narrow">
                          Versie
                        </th>
                        <th scope="col">Geldig tot</th>
                        <th scope="col" className="dir-hide-narrow">
                          Gekoppeld
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {docRows.map((x) => (
                        <tr key={x.id} aria-current={sel === x.id} onClick={() => select({ document: x.id })}>
                          <td className="tcell-title">
                            <button type="button" className="linkbtn inline" onClick={() => select({ document: x.id })}>
                              {x.title}
                            </button>
                            <div className="muted small">
                              {x.type.name}
                              {x.reference && ` · ${x.reference}`}
                              {x.visibility !== "intern" && ` · ${DOC_VISIBILITY_LABELS[x.visibility]}`}
                              {x.current.annotationCount > 0 && (
                                <>
                                  {" "}
                                  · <MarkedChip />
                                </>
                              )}
                            </div>
                          </td>
                          <td className="dir-hide-narrow">{DOC_CATEGORY_LABELS[x.type.category]}</td>
                          <td className="num dir-hide-narrow">{x.current.version}</td>
                          <td>
                            <StatusChip doc={x} />
                          </td>
                          <td className="dir-hide-narrow dir-wrap small">{linkSummary(x)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!docRows.length && (
                    <p className="muted">
                      {docs.length ? "Geen documenten gevonden." : "Nog geen documenten in het dossier van dit gebouw."}
                    </p>
                  )}
                </>
              )}
              {tab === "tekeningen" && (
                <>
                  <table className="it" aria-label="Tekeningen">
                    <thead>
                      <tr>
                        <th scope="col">Nummer</th>
                        <th scope="col">Titel</th>
                        <th scope="col" className="dir-hide-narrow">
                          Discipline
                        </th>
                        <th scope="col">Rev.</th>
                        <th scope="col" className="dir-hide-narrow">
                          Gekoppeld
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawingRows.map((x) => (
                        <tr key={x.id} aria-current={sel === x.id} onClick={() => select({ document: x.id })}>
                          <td className="code">{x.drawingNumber ?? "–"}</td>
                          <td className="tcell-title">
                            <button type="button" className="linkbtn inline" onClick={() => select({ document: x.id })}>
                              {x.title}
                            </button>
                            {(x.floor || x.current.annotationCount > 0) && (
                              <div className="muted small">
                                {x.floor?.name}
                                {x.floor && x.current.annotationCount > 0 && " · "}
                                {x.current.annotationCount > 0 && <MarkedChip />}
                              </div>
                            )}
                          </td>
                          <td className="dir-hide-narrow">{x.discipline ?? ""}</td>
                          <td>{x.revision ?? ""}</td>
                          <td className="dir-hide-narrow dir-wrap small">{linkSummary(x)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!drawingRows.length && (
                    <p className="muted">
                      {drawings.length
                        ? "Geen tekeningen gevonden."
                        : "Nog geen tekeningen. Upload installatie- of bouwtekeningen en koppel ze aan de assets."}
                    </p>
                  )}
                </>
              )}
              {tab === "certificaten" && (
                <>
                  <p className="muted small">
                    Het laatste certificaat per keuring. Nieuwe certificaten komen via Onderhoud en keuringen ›
                    Uitvoering vastleggen.
                  </p>
                  <table className="it" aria-label="Certificaten">
                    <thead>
                      <tr>
                        <th scope="col">Asset</th>
                        <th scope="col">Keuring</th>
                        <th scope="col" className="dir-hide-narrow">
                          Uitgevoerd
                        </th>
                        <th scope="col">Volgende</th>
                        <th scope="col">
                          <span className="sr-only">Openen</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {certs.map((c) => (
                        <tr key={c.recordId}>
                          <td>
                            {c.asset ? (
                              <Link to={`${buildingPath(id, "keuringen")}?asset=${c.asset.id}`}>
                                <span className="code">{c.asset.code}</span> {c.asset.name}
                              </Link>
                            ) : (
                              <Link to={`${buildingPath(id, "keuringen")}?gebouw=1`}>Gebouw</Link>
                            )}
                          </td>
                          <td>{c.obligation}</td>
                          <td className="dir-hide-narrow">{fmtDate(c.performedOn)}</td>
                          <td>{c.nextDueOn ? fmtDate(c.nextDueOn) : "–"}</td>
                          <td>
                            <a className="btn small ghost" href={fileUrl(c.fileId)} target="_blank" rel="noreferrer">
                              Openen
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {tab === "plattegronden" && (
                <>
                  <p className="muted small">
                    De plattegronden van de verdiepingen. Vervangen gaat via Gebouw › Verdiepingen.
                  </p>
                  <ul className="dir-items panel doc-plans">
                    {plans.map((p) => (
                      <li key={p.floorId}>
                        <span className="grow">
                          <strong>{p.floorName}</strong>
                          <span className="muted small block">{p.fileName}</span>
                        </span>
                        <Link
                          className="btn small ghost"
                          to={`${buildingPath(id, "tekening")}?verdieping=${p.floorId}`}
                        >
                          <IconPin size={18} aria-hidden="true" /> Tekening
                        </Link>
                        <a className="btn small ghost" href={fileUrl(p.fileId)} target="_blank" rel="noreferrer">
                          <IconDownload size={18} aria-hidden="true" /> Openen
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            {hasSel && (
              <DocumentPanel
                key={sel}
                id={sel}
                canEdit={canEdit}
                canDelete={d.canDelete}
                showLog={me?.role !== "klant"}
                buildingId={id}
                onClose={() => select({ document: null })}
                onDialog={setDialog}
              />
            )}
          </div>
        </>
      )}

      {dialog?.type === "scan" && (
        <ScanDialog
          onClose={() => setDialog(null)}
          onDone={(file) => setDialog({ type: "upload", typeKey: dialog.typeKey, files: [file] })}
        />
      )}
      {dialog?.type === "usage" && <UsageDialog building={b} onClose={() => setDialog(null)} />}
      {dialog?.type === "upload" && types.data && (
        <UploadDialog
          building={b}
          types={types.data}
          preset={{ drawing: dialog.drawing, typeKey: dialog.typeKey }}
          files={dialog.files}
          onScan={dialog.drawing ? undefined : () => setDialog({ type: "scan", typeKey: dialog.typeKey })}
          onClose={() => setDialog(null)}
          onDone={(ids) => ids.length === 1 && select({ document: ids[0] })}
        />
      )}
      {dialog?.type === "version" && <VersionDialog doc={dialog.doc} onClose={() => setDialog(null)} />}
      {dialog?.type === "edit" && types.data && (
        <EditDialogWithPeople doc={dialog.doc} building={b} types={types.data} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "links" && <LinksDialog doc={dialog.doc} building={b} onClose={() => setDialog(null)} />}
      {dialog?.type === "markup" && (
        <MarkupViewer
          doc={dialog.doc}
          version={dialog.version}
          canEdit={dialog.canEdit}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "share" && <ShareDialog doc={dialog.doc} onClose={() => setDialog(null)} />}
      {dialog?.type === "trash" && <TrashDialog buildingId={id} onClose={() => setDialog(null)} />}
    </div>
  );
}

function EditDialogWithPeople(props: Omit<Parameters<typeof EditDialog>[0], "members">) {
  const people = usePeople();
  const members = (people.data ?? []).map((p) => ({ id: p.userId, name: p.name }));
  // Huidige verantwoordelijke blijft kiesbaar, ook als de lijst (rechten) hem niet bevat.
  const r = props.doc.responsible;
  if (r && !members.some((m) => m.id === r.id)) members.unshift(r);
  return <EditDialog {...props} members={members} />;
}

function linkSummary(d: DocumentDto) {
  if (!d.links.length) return <span className="muted">–</span>;
  const first = d.links[0]!;
  const label = first.code ? `${first.code} ${first.name}` : first.name;
  return d.links.length > 1 ? `${label} +${d.links.length - 1}` : label;
}

function DocumentPanel({
  id,
  buildingId,
  canEdit,
  canDelete,
  showLog,
  onClose,
  onDialog,
}: {
  id: string;
  buildingId: string;
  canEdit: boolean;
  canDelete: boolean;
  /** Logboek is intern: niet voor klanten. */
  showLog: boolean;
  onClose: () => void;
  onDialog: (d: Dialog) => void;
}) {
  const toast = useToast();
  const refresh = useDossierRefresh(buildingId);
  const q = useDocument(id);
  const doc = q.data;
  if (!doc)
    return (
      <aside className="dir-panel" aria-label="Document" aria-busy="true">
        {q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />}
      </aside>
    );
  const remove = async () => {
    if (!window.confirm(`${doc.title} naar de prullenbak verplaatsen? Je kunt het 30 dagen terugzetten.`)) return;
    try {
      await api(`/documents/${doc.id}`, { method: "DELETE" });
      onClose();
      await refresh(doc.id);
      toast("Naar de prullenbak verplaatst");
    } catch (err) {
      toast(errMsg(err));
    }
  };
  const restoreVersion = async (n: number) => {
    if (!window.confirm(`Versie ${n} terugzetten? Die wordt dan de nieuwste versie; niets gaat verloren.`)) return;
    try {
      await api(`/documents/${doc.id}/versions/${n}/restore`, { method: "POST" });
      await refresh(doc.id);
      toast(`Versie ${n} teruggezet`);
    } catch (err) {
      toast(errMsg(err));
    }
  };
  const edit = canEdit && doc.canEdit;
  const v = doc.current;
  return (
    <aside className="dir-panel" aria-label={doc.title}>
      <header className="dir-panelhead">
        <div className="grow">
          <h2>{doc.title}</h2>
          <div className="dir-sub">
            {doc.type.name}
            {v.annotationCount > 0 && (
              <>
                {" "}
                · <MarkedChip />
              </>
            )}
            {doc.visibility !== "intern" && (
              <>
                {" "}
                ·{" "}
                <span className={`chip doc-vis doc-vis-${doc.visibility}`}>
                  {DOC_VISIBILITY_LABELS[doc.visibility]}
                </span>
              </>
            )}
          </div>
        </div>
        {edit && (
          <button type="button" className="btn small ghost" onClick={() => onDialog({ type: "edit", doc })}>
            <IconPencil aria-hidden="true" /> Bewerken
          </button>
        )}
        <button type="button" className="btn ghost small ico" onClick={onClose} aria-label="Paneel sluiten">
          <IconClose aria-hidden="true" />
        </button>
      </header>
      <section className="dir-sec doc-actions">
        <a className="btn primary small" href={fileUrl(v.fileId)} target="_blank" rel="noreferrer">
          <IconDownload size={18} aria-hidden="true" /> Openen
        </a>
        {isPdfDoc(v) && (edit || v.annotationCount > 0) && (
          <button
            type="button"
            className="btn small"
            onClick={() => onDialog({ type: "markup", doc, version: v, canEdit: edit })}
          >
            <IconMarkup size={18} aria-hidden="true" /> {edit ? "Markeren" : `Markeringen (${v.annotationCount})`}
          </button>
        )}
        {edit && (
          <>
            <button type="button" className="btn small" onClick={() => onDialog({ type: "version", doc })}>
              <IconUpload size={18} aria-hidden="true" /> Nieuwe versie
            </button>
            <button type="button" className="btn small" onClick={() => onDialog({ type: "links", doc })}>
              Koppelen
            </button>
            <button type="button" className="btn small" onClick={() => onDialog({ type: "share", doc })}>
              <IconShare size={18} aria-hidden="true" /> Delen
            </button>
          </>
        )}
        {canDelete && (
          <button type="button" className="btn small ghost danger" onClick={() => void remove()}>
            <IconTrash size={18} aria-hidden="true" /> Verwijderen
          </button>
        )}
      </section>
      <section className="dir-sec">
        <h3>Gegevens</h3>
        <dl className="kv">
          <dt>Rubriek</dt>
          <dd>{DOC_CATEGORY_LABELS[doc.type.category]}</dd>
          {doc.reference && (
            <>
              <dt>Kenmerk</dt>
              <dd>{doc.reference}</dd>
            </>
          )}
          {doc.drawingNumber && (
            <>
              <dt>Tekening</dt>
              <dd>
                <span className="code">{doc.drawingNumber}</span>
                {doc.revision && ` rev. ${doc.revision}`}
                {doc.discipline && ` · ${doc.discipline}`}
              </dd>
            </>
          )}
          {doc.floor && (
            <>
              <dt>Verdieping</dt>
              <dd>{doc.floor.name}</dd>
            </>
          )}
          {doc.issuer && (
            <>
              <dt>Afgegeven door</dt>
              <dd>{doc.issuer}</dd>
            </>
          )}
          {v.issuedOn && (
            <>
              <dt>Afgegeven op</dt>
              <dd>{fmtDate(v.issuedOn)}</dd>
            </>
          )}
          {v.validUntil && (
            <>
              <dt>Geldig tot</dt>
              <dd>
                <StatusChip doc={doc} /> <span className="muted small">{DOC_STATUS_LABELS[doc.status]}</span>
              </dd>
            </>
          )}
          {doc.responsible && (
            <>
              <dt>Verantwoordelijke</dt>
              <dd>{doc.responsible.name}</dd>
            </>
          )}
          <dt>Bestand</dt>
          <dd className="dir-wrap">
            {v.fileName} <span className="muted small">{fmtSize(v.size)}</span>
          </dd>
        </dl>
        {doc.notes && <p className="small dir-notes">{doc.notes}</p>}
      </section>
      <section className="dir-sec">
        <h3>Gekoppeld aan</h3>
        {doc.links.length === 0 ? (
          <p className="muted small">
            Niet gekoppeld. {edit && "Koppel een handleiding of tekening aan de assets waar hij bij hoort."}
          </p>
        ) : (
          <ul className="dir-items">
            {doc.links.map((l) => (
              <li key={`${l.kind}-${l.id}`}>
                {l.kind === "asset" ? (
                  <Link className="grow" to={`${buildingPath(buildingId, "assets")}?asset=${l.id}`}>
                    <span className="code">{l.code}</span> {l.name}
                    {l.hasParts && <span className="muted small"> · en onderdelen</span>}
                  </Link>
                ) : (
                  <span className="grow">
                    {l.name} <span className="muted small">ruimte</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="dir-sec">
        <h3>
          <span>
            <IconHistory size={14} aria-hidden="true" /> Versies
          </span>
        </h3>
        <ol className="dir-items doc-versions">
          {doc.versions.map((x) => (
            <li key={x.id} aria-current={x.version === v.version}>
              <span className="grow">
                <strong>Versie {x.version}</strong>
                {x.revision && ` · rev. ${x.revision}`}
                {x.version === v.version && <span className="chip doc-now">Huidig</span>}
                <span className="muted small block">
                  {fmtDate(x.createdAt)}
                  {x.createdBy && ` · ${x.createdBy}`}
                  {x.validUntil && ` · geldig tot ${fmtDate(x.validUntil)}`}
                </span>
                {x.note && <span className="small block">{x.note}</span>}
                {x.annotationCount > 0 && (
                  <button
                    type="button"
                    className="linkbtn small block"
                    onClick={() => onDialog({ type: "markup", doc, version: x, canEdit: edit })}
                  >
                    {x.annotationCount} {x.annotationCount === 1 ? "markering" : "markeringen"}
                  </button>
                )}
              </span>
              <a
                className="btn small ghost"
                href={fileUrl(x.fileId)}
                target="_blank"
                rel="noreferrer"
                aria-label={`Versie ${x.version} openen`}
              >
                <IconDownload size={18} aria-hidden="true" />
              </a>
              {edit && x.version !== v.version && (
                <button type="button" className="btn small ghost" onClick={() => void restoreVersion(x.version)}>
                  Terugzetten
                </button>
              )}
            </li>
          ))}
        </ol>
      </section>
      {showLog && (
        <Logbook
          entity="document"
          id={doc.id}
          path={`/documents/${doc.id}/log`}
          buildingId={buildingId}
          className="dir-sec logbook"
        />
      )}
    </aside>
  );
}
